"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-lg" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-lg" />
        <div className="h-9 w-20 bg-muted/50 animate-pulse rounded-lg" />
      </div>;
  }
  return <NavigationMenu viewport={true}>
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-primary-foreground hover:text-secondary transition-colors data-[state=open]:text-secondary font-default">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-card border border-border shadow-xl rounded-lg">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                            <div className="text-sm font-bold leading-none font-default">
                              {child.label}
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild className={cn(navigationMenuTriggerStyle(), "bg-transparent text-primary-foreground hover:text-secondary transition-colors font-default")}>
                <Link to={item.href || "#"}>{item.label}</Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-lg" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-lg" />
        <div className="h-10 w-full bg-muted/50 animate-pulse rounded-lg" />
      </div>;
  }
  return <div className="flex flex-col p-4 space-y-2">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col space-y-2">
          <Link to={item.href || "#"} className="text-lg font-bold font-default py-2 border-b border-border/30 text-foreground" onClick={onItemClick}>
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="flex flex-col pl-4 space-y-2 mt-2">
              {item.children.map((child, j) => <Link key={child.id} data-index={j} to={child.href || "#"} className="text-base text-muted-foreground hover:text-primary py-1 font-default" onClick={onItemClick}>
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <>
      <section data-section-id="259734" id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-500 bg-primary text-primary-foreground border-b border-border/10", isScrolled && "bg-primary/90 backdrop-blur-xl shadow-lg")}>
        <div className="container mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          
          <div className="flex items-center shrink-0 gap-3">
            <Link to="/HomePage" className="flex items-center gap-2">
              <span className="text-2xl font-bold tracking-tighter font-serif">JUNKR Ottawa</span>
            </Link>
            <Button asChild variant="ghost" size="icon" className="text-primary-foreground hover:bg-white/10 rounded-lg">
              <Link to="tel:+16135550123">
                <Phone className="size-5" />
              </Link>
            </Button>
          </div>

          
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="44">
              <DesktopMenuConsumer />
            </MenuProvider>
          </div>

          
          <div className="hidden md:flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-primary-foreground/60 font-mono uppercase tracking-widest">Mode</span>
              <Switch />
            </div>
            <Button variant="secondary" size="sm" className="font-default font-bold rounded-lg px-6 bg-secondary text-secondary-foreground hover:scale-105 transition-transform shadow-md">
              Get a Quote
            </Button>
          </div>

          
          <div className="flex md:hidden items-center gap-4">
            <Switch className="scale-75" />
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" className="text-primary-foreground">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>

        
        {isMobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border/20 shadow-2xl">
            <MenuProvider menu_id="44">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            <div className="p-4 border-t border-border/10">
              <Button variant="secondary" className="w-full font-default font-bold rounded-lg bg-secondary text-secondary-foreground">
                Get a Quote
              </Button>
            </div>
          </div>}
      </section>

      
      {isMobile && <div className="md:hidden fixed bottom-0 inset-x-0 z-50 p-4 bg-background/95 backdrop-blur-md border-t border-border/20 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <Button asChild variant="secondary" className="w-full h-12 font-bold rounded-lg bg-secondary text-secondary-foreground shadow-lg">
            <Link to="#contact">
              Get Free Quote
            </Link>
          </Button>
        </div>}
    </>;
}